import { z } from 'zod'

import {
  AbstractSelectionStatus,
  AbstractType,
  AbstractRolesType,
  AbstractSource,
  AbstractStatus,
} from '../../types'
import { preprocessId, preprocessBoolean } from '../../utils'
import type { Abstracts, Departments } from '../../types'
import i18n from '../../i18n'
import { PostAbstractReviews } from '../../services/events'

export const AuthorsSchema = z.object({
  user: z.object({
    id: preprocessId(),
    first_name: z.string(),
    last_name: z.string(),
  }),
  roles: z.array(z.nativeEnum(AbstractRolesType)).min(1),
})
export type AuthorsSchema = z.infer<typeof AuthorsSchema>

export const DepartmentSchema = z.object({
  id: preprocessId(),
  name: z.string().nullable(),
  facility_id: z.number().nullable(),
})
export type DepartmentSchema = z.infer<typeof DepartmentSchema>

export const AbstractSchema = z.object({
  id: preprocessId(),
  authors: z.array(AuthorsSchema).superRefine((val, ctx) => {
    const roles = val.reduce(
      (acc, elem) => {
        const innerRoles = elem.roles.reduce(
          (acc, role) => {
            acc.push(role)
            return acc
          },
          [] as AuthorsSchema['roles']
        )

        acc.push(...innerRoles)
        return acc
      },
      [] as AuthorsSchema['roles']
    )

    const hasAuthor = roles.includes(AbstractRolesType.Author)
    const hasOrator = roles.includes(AbstractRolesType.Speaker)
    if (!hasAuthor || !hasOrator) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          i18n.t('La liste des auteurs doit au moins contenir un auteur principal et un orateur') +
          '.',
      })
    }
  }),
  figures: z
    .array(
      z.object({
        id: preprocessId(),
        image: z.string(),
        title: z.string(),
      })
    )
    .max(3),
  type: z.nativeEnum(AbstractType).default(AbstractType['']),
  source: z.nativeEnum(AbstractSource),
  source_other: z.string().optional(),
  title: z.string().min(1),
  objective: z.string().min(1),
  materials_and_methods: z.string().min(1),
  results: z.string().min(1),
  conclusions: z.string().min(1),
  title_en: z.string().optional(),
  objective_en: z.string().optional(),
  materials_and_methods_en: z.string().optional(),
  results_en: z.string().optional(),
  conclusions_en: z.string().optional(),
  status: z.nativeEnum(AbstractStatus),
  selection_status: z.nativeEnum(AbstractSelectionStatus).optional(),
  event: preprocessId(),
  domain: z.number(),
  theme: z.number(),
  department: DepartmentSchema.nullable(),
  department_other: z.string().optional().default(''),
  keywords: z.array(z.number()).optional(),
  _new: preprocessBoolean().default(false),
}) satisfies z.ZodType<Omit<Abstracts, 'owner' | 'department'>, z.ZodTypeDef, unknown>
export type AbstractSchema = z.infer<typeof AbstractSchema>

export const AbstractReviewsSchema = z
  .object({
    score: z.number().min(0).max(10).nullable(),
    proposed_topic_session: z.coerce.string(),
    comment: z.string(),
    has_conflict_of_interest: preprocessBoolean().nullish(),
    conflict_of_interest: z.string(),
  })
  .superRefine((val, ctx) => {
    if (!!val.has_conflict_of_interest && !val.conflict_of_interest) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('Le champ est obligatoire') + '.',
      })
    }
  })
  .transform(val => {
    // If the user has a conflict of interest, we reset the score, comment and proposed_topic_session
    if (!!val.has_conflict_of_interest) {
      val.comment = ''
      val.score = null
      val.proposed_topic_session = ''
    }
    // If the user does not have a conflict of interest, we reset the conflict_of_interest
    if (!val.has_conflict_of_interest) {
      val.conflict_of_interest = ''
    }

    return val
  }) satisfies z.ZodType<PostAbstractReviews, z.ZodTypeDef, unknown>
export type AbstractReviewsSchema = z.infer<typeof AbstractReviewsSchema>
